import { isAllowedProtocol, parseChangeUrl } from './parseChangeUrl';

const PRODUCTION_APP_HOSTNAME = 'www.change.org';
const PRODUCTION_APEX_HOSTNAME = 'change.org';

/**
 * Paths that aren't pages: APIs, routes that act on GET, redirectors, SSR mutation pages, and
 * staff/infra routes. Matched case-insensitively on the normalised pathname, at a segment boundary,
 * so `/api` blocks `/api` and `/api/x` but not `/apiary`.
 *
 * Add a route that mutates on load, or an Express route reachable on the www origin, here in the
 * same PR that creates it.
 */
const NON_PAGE_PATH_PATTERNS: RegExp[] = [
	// API and internals
	/^\/api-proxy(\/|$)/,
	/^\/api(\/|$)/,
	/^\/graphql(\/|$)/,
	/^\/schema\.graphql(\/|$)/,
	/^\/\.well-known(\/|$)/,
	// action-on-GET and redirector routes
	/^\/logout(\/|$)/,
	/^\/redirect(\/|$)/,
	/^\/careers(\/|$)/,
	/^\/external-redirect(\/|$)/,
	/^\/[^/]+_share_redirect(\/|$)/,
	/^\/p\/[^/]+\/sign(\/|$)/,
	/^\/p\/[^/]+\/sign-now(\/|$)/,
	/^\/p\/[^/]+\/signatures\/confirm(\/|$)/,
	/^\/p\/[^/]+\/feedback(\/|$)/,
	/^\/u\/me\/feedback(\/|$)/,
	// SSR mutation pages
	/^\/verify_new_user(\/|$)/,
	/^\/verify_your_email(\/|$)/,
	/^\/account_settings\/confirm_email(\/|$)/,
	/^\/unsubscribe(\/|$)/,
	/^\/p\/[^/]+\/unsign(\/|$)/,
	// staff and infra
	/^\/staff-utils(\/|$)/,
	/^\/staff-utils-public(\/|$)/,
	/^\/corgi(\/|$)/,
	/^\/corgi-middle-end(\/|$)/,
	// static files (manifest.json, serviceworker.js, register_sw.js, robots.txt, ...)
	/\.(js|json|txt)\/?$/,
];

const ABSOLUTE_HTTP_URL = /^https?:\/\//i;

const UNRESERVED_CHARACTER = /^[A-Za-z0-9\-._~]$/;

/**
 * Whether a pathname holds a percent-escape that hides a letter, digit, unreserved character, a
 * separator (`/`, `\`) or NUL. Standard encoders never escape those, and a server that decodes
 * would see through them (`/%61pi-proxy` is `/api-proxy`).
 */
function hasDisguisingEscape(pathname: string): boolean {
	for (const [, hex] of pathname.matchAll(/%([0-9a-fA-F]{2})/g)) {
		const character = String.fromCharCode(Number.parseInt(hex as string, 16));
		if (UNRESERVED_CHARACTER.test(character) || character === '/' || character === '\\' || character === '\0') {
			return true;
		}
	}
	return false;
}

function isAllowedHost(parsedUrl: URL, parsedBaseUrl: URL): boolean {
	const isProductionApex =
		parsedBaseUrl.hostname === PRODUCTION_APP_HOSTNAME &&
		parsedUrl.hostname === PRODUCTION_APEX_HOSTNAME &&
		parsedUrl.port === '';
	return parsedUrl.host === parsedBaseUrl.host || isProductionApex;
}

function isPagePathname(pathname: string): boolean {
	if (pathname.startsWith('//') || pathname.startsWith('/\\') || hasDisguisingEscape(pathname)) {
		return false;
	}
	// nginx merges repeated slashes before routing, so `/p/1//sign-now` must match `/p/1/sign-now`
	const lowerCasePathname = pathname.toLowerCase().replace(/\/{2,}/g, '/');
	return !NON_PAGE_PATH_PATTERNS.some((pattern) => pattern.test(lowerCasePathname));
}

/**
 * Whether `url` is a Change.org page that's safe to redirect to automatically: same origin as
 * `baseUrl` (plus the production apex), a page path rather than an API, redirector or mutation
 * route, https (or http when `baseUrl` is http), and no credentials.
 *
 * `baseUrl` is the origin the caller is serving from. It must be a Change app host or this returns
 * `false`. Take it from `utilityContext.resolveUrl('/')` in the front-end and `webappInfo.appHost`
 * in the middle-end, never from `req.hostname`.
 *
 * Only root-relative paths and absolute URLs are accepted. Never throws.
 */
export function isChangePageUrl(url: string, { baseUrl }: { baseUrl: string }): boolean {
	// Anything but a root-relative path or an explicit `http(s)://` URL (`foo`, `?x=1`, `https:foo`)
	// resolves differently here than in the navigation that follows
	if (!url.startsWith('/') && !ABSOLUTE_HTTP_URL.test(url)) {
		return false;
	}

	const parsed = parseChangeUrl(url, baseUrl);
	return (
		!!parsed &&
		isAllowedProtocol(parsed.parsedUrl, parsed.parsedBaseUrl) &&
		isAllowedHost(parsed.parsedUrl, parsed.parsedBaseUrl) &&
		isPagePathname(parsed.parsedUrl.pathname)
	);
}
